import { FullscreenPreview } from '../../../../general/components/FullscreenPreview/FullscreenPreview';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch, useHistory, generatePath, Link } from 'react-router-dom';
import { Asset, urlLinkedCreative } from '../../../api';
import * as finderActions from '../../finder/actions';
import { getData, getDetails, getNavigation } from '../../finder/selectors';
import { routes } from '../../router/model';
import { FileViewer } from '../../../../general/components/FullscreenPreview/FileViewer';
import '../style.scss';
import { getFileViewerLink } from '../../../../general/components/FullscreenPreview/model';
import { Spin } from '@myua/kit';
import { ErrorMessage } from './ErrorMessage';
import { creativeHubExternalURLRaw } from '../../finder/model';
import { CloseOutlined } from '@ant-design/icons';
import { urlDirectDownload } from '../../../../general/urls';
import { NodeFlag } from '../../../../general/model';

const FooterContent: React.FC<{
  currentAssetID: number;
  currentCreativeID?: number;
}> = ({ currentAssetID, currentCreativeID }) => {
  return (
    <div className="ch-file-viewer__chub-link">
      <a
        target="_blank"
        rel="noreferrer"
        href={creativeHubExternalURLRaw(currentAssetID, currentCreativeID)}
      >
        Open in Creative Hub
      </a>
    </div>
  );
};

const LinkedCreativePreviewInner: React.FC<{
  sharingId: string | undefined;
  onClose: () => void;
  currentAsset: Asset;
  back: string;
}> = ({ sharingId, onClose, currentAsset, back }) => {
  const gallery = currentAsset.creatives;

  // state
  const [creativeIndex, setCreativeIndex] = useState<number | null>(null);

  // selectors

  // fx
  useEffect(() => {
    setCreativeIndex(0);
  }, [gallery]);

  // render

  if (gallery.length === 0)
    return (
      <div className="ch-file-viewer__grid">
        <span style={{ gridArea: 'main', placeSelf: 'center' }}>Gallery is empty</span>
        <div
          style={{ gridArea: 'head', placeSelf: 'end' }}
          className="ch-file-viewer__btn gen-file-viewer__btn_top"
          title={'Close'}
          onClick={onClose}
        >
          <CloseOutlined />
        </div>
        <div className="ch-file-viewer__bottom">
          <FooterContent currentAssetID={currentAsset.id} />
        </div>
      </div>
    );

  const onNext = () => setCreativeIndex((x) => (x === null ? 0 : (x + 1) % gallery.length));
  const onPrev = () =>
    setCreativeIndex((x) => (x === null ? 0 : x === 0 ? gallery.length - 1 : x - 1));

  const currCreative = creativeIndex === null ? null : gallery[creativeIndex];

  const url = currCreative
    ? getFileViewerLink(
        currCreative.name,
        urlLinkedCreative('download', currentAsset.id ?? 0, currCreative.id, sharingId),
        urlLinkedCreative('preview', currentAsset.id ?? 0, currCreative.id, sharingId),
      )
    : undefined;

  if (creativeIndex === null || url === undefined) {
    return <MySpin />;
  }

  return (
    <FileViewer
      footerContent={
        <FooterContent currentAssetID={currentAsset.id} currentCreativeID={currCreative?.id} />
      }
      googleDriveProvider={{
        url: urlDirectDownload(currentAsset),
      }}
      figmaImportProvider={{
        node: currentAsset.flags.includes(NodeFlag.figma_file) ? currentAsset : null,
      }}
      compressedFileTreeProvider={null}
      imageMetadataProvider={null}
      quickShareProvider={null}
      position={
        <span>
          {creativeIndex + 1} of {gallery.length}
        </span>
      }
      description={
        <Link
          to={generatePath(back + routes.preview, {
            assetID: currentAsset.id,
            sharingId,
          })}
        >
          Watch asset
        </Link>
      }
      filename={currCreative?.name ?? ''}
      onNext={onNext}
      onPrev={onPrev}
      onClose={onClose}
      url={url}
      shareAssetButtonCallback={null}
      shareAssetInProgress={false}
    />
  );
};

export const LinkedCreativePreview: React.FC<{ back: string }> = ({ back }) => {
  const { curr } = useSelector(getNavigation);
  const { assets } = useSelector(getData);
  const error = useSelector(getDetails).error;
  const dispatch = useDispatch();
  const history = useHistory();

  const loc = useRouteMatch<{ assetID: string; sharingId?: string }>(back + routes.previewCreative);

  const assetIDInLocation = parseInt(loc?.params.assetID ?? '', 10);

  const sharingId = loc?.params.sharingId;

  const onClose = useCallback(() => {
    history.replace(
      generatePath(back, {
        sharingId,
      }),
    );
  }, [history, back, sharingId]);

  useEffect(() => {
    const onKeyDown = ({ key }: KeyboardEvent) => {
      if (key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    const currID = curr?.id;
    if (assets.length !== 0 && assetIDInLocation !== currID) {
      // Ассеты загружены, но URL и selectedID не соответствуют друг другу

      if (currID !== undefined) {
        // у нас существует выбранный ассет, а значит он выбран пользователем, слушаемся его и меняем урл
        history.replace(
          generatePath(back + routes.previewCreative, {
            assetID: currID,
            sharingId,
          }),
        );
      } else {
        // currID === undefined, а значит страница была только что загружена
        dispatch(
          finderActions.assetSelected({
            ids: [assetIDInLocation],
            firstId: assetIDInLocation,
            lastId: assetIDInLocation,
          }),
        );
      }
    }
  }, [assets, assetIDInLocation, curr, sharingId, dispatch, history, back]);

  return (
    <FullscreenPreview onClose={onClose}>
      <div className="al-fullscreen-preview">
        {error ? (
          <ErrorMessage error={error} onClose={onClose} />
        ) : curr === null ? (
          <MySpin />
        ) : (
          <LinkedCreativePreviewInner
            back={back}
            sharingId={sharingId}
            onClose={onClose}
            currentAsset={curr}
          />
        )}
      </div>
    </FullscreenPreview>
  );
};

const MySpin = () => (
  <div
    style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}
  >
    <Spin size="large" />
  </div>
);
